import { Close, FastForward, HighlightOff } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { consumerAPI } from 'api/consumerAPI'
import { BookingSupplierStatus } from 'api/models/Booking.type'
import DirectionsMap from 'components/Common/DirectionsMap/DirectionsMap'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

export function FlexibleBookingControl() {
  const [cancelOpen, setCancelOpen] = useState(false)
  const [showError, setShowError] = useState(false)
  const [applicationTheme, setApplicationTheme] = useState('Jyrney')
  const { path: communicationReference } = useParams<{ path: string }>()
  console.log('commsRef', communicationReference)

  const {
    data: flexibleBookingData,
    error: fetchError,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['flexibleBooking', communicationReference],
    queryFn: async () => {
      const { data } = await consumerAPI.getFlexibleBooking(
        communicationReference,
      )
      return data
    },
    enabled: !!communicationReference,

    refetchInterval: (data) => {
      if (!data) return 1000
      const status = data.status
      if (status === 'Cancelled' || status === 'Complete') return false
      if (status === 'Allocated') return 2000
      return 5000
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,

    onSuccess: (data) => {
      if (data?.applicationTheme) setApplicationTheme(data.applicationTheme)
    },

    staleTime: 1500,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  })

  const {
    data: updateFlexBooking,
    error: updateError,
    isLoading: updateLoading,
    mutate,
  } = useMutation({
    mutationFn: async (status: 'ready' | 'cancel') => {
      return consumerAPI.updateFlexibleBooking(communicationReference, status)
    },
    onSuccess: () => {
      refetch()
    },
    onError: () => {
      setShowError(true)
    },
  })

  const handleClose = (e: React.MouseEvent) => {
    setCancelOpen(false)
    if (e.target.id === 'process-cancel') {
      mutate('cancel')
    }
  }

  useEffect(() => {
    if (fetchError || updateError) {
      setShowError(true)
    }
  }, [fetchError, updateError])
  console.log(updateFlexBooking, updateError)
  console.log('>>>>', flexibleBookingData, fetchError, isLoading)

  return (
    <Box
      id="flexbox"
      sx={{
        display: 'flex',
        flexGrow: 1,
        width: '100dvw',
        height: '100dvh',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: applicationTheme !== 'Veezu' ? 'var(--jy-navy-900, #00253d)' : '#000',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          maxWidth: '90dvw',
          maxHeight: '100dvh',
          borderRadius: '8px',
        }}
      >
        {isLoading && !flexibleBookingData ? (
          <>
            <CircularProgress />
            <Typography>Fetching booking information...</Typography>
          </>
        ) : (
          <>
            {fetchError && (
              <>
                <HighlightOff fontSize="large" />
                <Typography>Unable to fetch booking information</Typography>
                <Typography fontFamily={'monospace'}>
                  {fetchError &&
                  typeof fetchError === 'object' &&
                  'message' in fetchError
                    ? (fetchError as { message?: string }).message
                    : String(fetchError)}
                </Typography>
              </>
            )}
          </>
        )}

        {flexibleBookingData && (
          <>
            <Box>
              {applicationTheme === 'Veezu' ? (
                <img
                  src="/static/media/Veezu-WhiteCoral.8554a5a79edee00b5bca514d349e0ad0.svg"
                  alt="Veezu Logo"
                  style={{ width: '200px', height: 'auto' }}
                />
              ) : (
                <img
                  src="/static/media/JYRNEY-Logo-WhiteOnClear-Horiz.b407a6d81dc6732a0b4b.png"
                  alt="Jyrney Logo"
                  style={{ width: '200px', height: 'auto' }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                backgroundColor: '#fff',
                padding: '20px',
                marginBottom: '20px',
                width: '100%',
                borderRadius: '4px',
                gap: '20px',
              }}
            >
              {flexibleBookingData.status === 'Allocated' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    <Typography variant="h6">Flexible Booking</Typography>
                    <Chip
                      color="primary"
                      label={
                        Object.entries(BookingSupplierStatus).filter(
                          (kv) => kv[0] === flexibleBookingData.status,
                        )[0][1]
                      }
                    />
                  </Box>
                  <Typography fontSize={'0.85rem'}>
                    All set? Tap the{' '}
                    <span style={{ fontWeight: 500 }}>I&apos;m ready</span>{' '}
                    button below and we&apos;ll send the driver right away.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      From:
                      <Chip
                        label={`${flexibleBookingData.pickup.formattedAddress}`}
                        sx={{
                          whiteSpace: 'normal',
                          paddingY: '0.5vh',
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            fontSize: 'clamp(0.75rem, 2vw, 1.2rem)',
                            lineHeight: 'clamp(1.2, 1.3, 1.4)',
                            overflow: 'visible',
                            textWrap: 'pretty',
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      To:
                      <Chip
                        label={`${flexibleBookingData.dropOff.formattedAddress}`}
                        sx={{
                          whiteSpace: 'normal',
                          paddingY: '0.5vh',
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            fontSize: 'clamp(0.75rem, 2vw, 1.2rem)',
                            lineHeight: 'clamp(1.2, 1.3, 1.4)',
                            overflow: 'visible',
                            textWrap: 'pretty',
                          },
                        }}
                      />
                    </Box>
                    <DirectionsMap
                      origin={flexibleBookingData.pickup}
                      destination={
                        flexibleBookingData.dropOff.addressLine1
                          ? flexibleBookingData.dropOff
                          : flexibleBookingData.pickup
                      }
                      boxShadow="#2e2e2e47 0px 2px 5px"
                      additionalStops={flexibleBookingData.additionalStops}
                      mapWidth="100%"
                      mapHeight="30dvh"
                      mapOptions={{ mapTypeControl: false, controlSize: 1 }}
                      borderRadius="8px"
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap-reverse',
                        width: '100%',
                        gap: '5px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Button
                        size="medium"
                        color="error"
                        onClick={() => setCancelOpen(true)}
                        sx={{ flex: '1 1 auto' }}
                      >
                        <Close sx={{ marginRight: '3px' }} />
                        Cancel booking
                      </Button>
                      <LoadingButton
                        loading={updateLoading}
                        disabled={updateLoading}
                        endIcon={<FastForward />}
                        loadingIndicator={
                          <CircularProgress color="inherit" size={16} />
                        }
                        sx={{
                          display: 'flex',
                          flex: '1 1 auto',
                          alignItems: 'center',
                          backgroundColor: 'green',
                          ':active': { backgroundColor: 'green' },
                        }}
                        onClick={() => {
                          mutate('ready')
                        }}
                      >
                        I&apos;m ready
                      </LoadingButton>
                    </Box>
                  </Box>
                </>
              )}
              {[
                'Confirmed',
                'OnRoute',
                'Arrived',
                'Passenger_Picked_Up',
                'Passenger_Dropped_Off',
                'OnRoute_Passenger_On_Board',
              ].includes(flexibleBookingData.status) && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    <Typography variant="h6">Flexible Booking</Typography>
                    <Chip
                      color="primary"
                      label={
                        Object.entries(BookingSupplierStatus).filter(
                          (kv) => kv[0] === flexibleBookingData?.status,
                        )[0][1]
                      }
                    />
                  </Box>
                  <Typography>
                    You&apos;re{' '}
                    {['Confirmed', 'OnRoute', 'Arrived'].includes(
                      flexibleBookingData.status,
                    ) && `almost`}{' '}
                    on your way.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      From:
                      <Chip
                        label={`${flexibleBookingData.pickup.formattedAddress}`}
                        sx={{
                          whiteSpace: 'normal',
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      To:
                      <Chip
                        label={`${flexibleBookingData.dropOff.formattedAddress}`}
                        sx={{
                          whiteSpace: 'normal',
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                          },
                        }}
                      />
                    </Box>
                    <DirectionsMap
                      origin={flexibleBookingData.pickup}
                      destination={
                        flexibleBookingData.dropOff.addressLine1
                          ? flexibleBookingData.dropOff
                          : flexibleBookingData.pickup
                      }
                      boxShadow="#2e2e2e47 0px 2px 5px"
                      additionalStops={flexibleBookingData.additionalStops}
                      mapWidth="100%"
                      mapHeight="30dvh"
                      mapOptions={{ mapTypeControl: false, controlSize: 1 }}
                      borderRadius="8px"
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexGrow: 1,
                        gap: '20px',
                        alignItems: 'center',
                      }}
                    >
                      {['Confirmed', 'OnRoute', 'Arrived'].includes(
                        flexibleBookingData.status,
                      ) ? (
                        <Button
                          color="error"
                          onClick={() => setCancelOpen(true)}
                        >
                          Cancel booking
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Box>
                  </Box>
                </>
              )}
              {flexibleBookingData.status === 'Cancelled' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    <Typography variant="h6">Flexible Booking</Typography>
                    <Chip
                      color="primary"
                      label={
                        Object.entries(BookingSupplierStatus).filter(
                          (kv) => kv[0] === flexibleBookingData?.status,
                        )[0][1]
                      }
                    />
                  </Box>
                  <Typography>Your booking has been cancelled.</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      From:
                      <Chip
                        label={`${flexibleBookingData.pickup.formattedAddress}`}
                        sx={{
                          whiteSpace: 'normal',
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      To:
                      <Chip
                        label={`${flexibleBookingData.dropOff.formattedAddress}`}
                        sx={{
                          whiteSpace: 'normal',
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                          },
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        flexGrow: 1,
                        gap: '20px',
                        alignItems: 'center',
                      }}
                    ></Box>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
      <Dialog open={cancelOpen} sx={{ gap: '10px', display: 'flex' }}>
        <DialogTitle
          id="alert-dialog-title"
          sx={{ backgroundColor: 'red', color: 'white !important' }}
        >
          {'Cancel booking'}
        </DialogTitle>
        <DialogContent
          sx={{
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <DialogContentText id="alert-dialog-description">
            This will cancel all the vehicles against this booking. Are you sure
            you want to cancel?
          </DialogContentText>
          <Alert severity="info">
            There may be a cancellation charge for this booking.
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            paddingX: '24px',
            marginBottom: '10px',
          }}
        >
          <Button id={'no-cancel'} color="primary" onClick={handleClose}>
            No
          </Button>
          <Button id={'process-cancel'} color="error" onClick={handleClose}>
            Yes, cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showError}>
        <DialogTitle
          id="alert-dialog-title"
          sx={{ backgroundColor: 'red', color: 'white !important' }}
        >
          {'Problem with your booking'}
        </DialogTitle>
        <DialogContent sx={{ marginTop: '20px' }}>
          <DialogContentText id="alert-dialog-description">
            There was an issue with your booking, please get in touch to resolve
            this issue.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            paddingX: '24px',
            marginBottom: '10px',
          }}
        >
          <Button color="primary" onClick={() => setShowError(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
